import { ref, onValue, off } from "firebase/database";

/**
 * Safely sets up a Firebase Realtime Database listener with proper error handling
 * @param {Object} database - Firebase database instance
 * @param {string} path - Database path
 * @param {Function} callback - Success callback function
 * @param {Function} errorCallback - Error callback function
 * @returns {Function} Unsubscribe function
 */
export const safeOnValue = (database, path, callback, errorCallback = null) => {
  try {
    const dbRef = ref(database, path);
    
    const unsubscribe = onValue(dbRef, (snapshot) => {
      try {
        if (snapshot && typeof callback === 'function') {
          callback(snapshot);
        }
      } catch (error) {
        console.error('Error in Firebase listener callback:', error);
        if (errorCallback && typeof errorCallback === 'function') {
          errorCallback(error);
        }
      }
    }, (error) => {
      console.error('Firebase database error:', error);
      if (errorCallback && typeof errorCallback === 'function') {
        errorCallback(error);
      }
    });

    return unsubscribe;
  } catch (error) {
    console.error('Error setting up Firebase listener:', error);
    if (errorCallback && typeof errorCallback === 'function') {
      errorCallback(error);
    }
    return () => {}; // Return empty function if setup fails
  }
};

/**
 * Safely cleans up a Firebase listener
 * @param {Function} unsubscribe - Unsubscribe function from onValue
 * @param {Object} database - Firebase database instance
 * @param {string} path - Database path (optional, for fallback cleanup)
 */
export const safeOff = (unsubscribe, database = null, path = null) => {
  try {
    if (unsubscribe && typeof unsubscribe === 'function') {
      unsubscribe();
    }
  } catch (error) {
    console.warn('Error during Firebase listener cleanup:', error);
    
    // Fallback cleanup if unsubscribe function fails
    if (database && path) {
      try {
        const dbRef = ref(database, path);
        off(dbRef);
      } catch (fallbackError) {
        console.warn('Fallback Firebase cleanup also failed:', fallbackError);
      }
    }
  }
};

/**
 * Creates a safe Firebase listener with automatic cleanup
 * @param {Object} database - Firebase database instance
 * @param {string} path - Database path
 * @param {Function} callback - Success callback function
 * @param {Function} errorCallback - Error callback function
 * @returns {Object} Object with unsubscribe function and ref
 */
export const createSafeListener = (database, path, callback, errorCallback = null) => {
  const dbRef = ref(database, path);
  const unsubscribe = safeOnValue(database, path, callback, errorCallback);
  
  return {
    unsubscribe,
    ref: dbRef,
    cleanup: () => safeOff(unsubscribe, database, path)
  };
};

/**
 * Batch cleanup multiple Firebase listeners
 * @param {Array} listeners - Array of listener objects with unsubscribe functions
 */
export const cleanupListeners = (listeners) => {
  if (!Array.isArray(listeners)) return;
  
  listeners.forEach((listener, index) => {
    try {
      if (listener && typeof listener.unsubscribe === 'function') {
        listener.unsubscribe();
      } else if (listener && typeof listener === 'function') {
        listener();
      }
    } catch (error) {
      console.warn(`Error cleaning up listener ${index}:`, error);
    }
  });
};
